<template>
  <div class="home-view">
    <!-- Quick Actions -->
    <div class="rounded-lg shadow-sm border mb-8">
      <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Button
          label="New Transaction"
          icon="pi pi-plus"
          @click="navigateToNewTransaction"
          class="p-button-outlined"
        />
        <Button
          label="View Transactions"
          icon="pi pi-list"
          @click="navigateToTransactions"
          class="p-button-outlined"
        />
        <Button
          label="Business Overview"
          icon="pi pi-chart-bar"
          @click="navigateToBusinessOverview"
          class="p-button-outlined"
        />
        <Button
          label="CRM Tasks"
          icon="pi pi-check-square"
          @click="navigateToTasks"
          class="p-button-outlined"
        />
        <Button
          label="Admin Panel"
          icon="pi pi-cog"
          @click="navigateToAdmin"
          class="p-button-outlined"
          severity="warning"
        />
      </div>
    </div>

    <div
      class="cards-container grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8 w-full mx-auto"
    >
      <!-- Today Summary -->
      <Card class="w-full">
        <template #title>
          <span class="text-blue-800 text-center block">Today</span>
        </template>
        <template #content>
          <div class="space-y-4">
            <div class="flex justify-between items-center">
              <span class="text-base font-semibold text-gray-700 mr-auto"
                >Purchases ({{ todayStats.purchases.count }}):</span
              >
              <div class="text-right">
                <span class="font-medium text-gray-700">
                  {{ formatCurrency(todayStats.purchases.amount) }} /
                  {{ formatWeight(todayStats.purchases.weight) }}
                </span>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-base font-semibold text-gray-700 mr-auto"
                >Sales ({{ todayStats.sales.count }}):</span
              >
              <div class="text-right">
                <span class="font-medium text-gray-700">
                  {{ formatCurrency(todayStats.sales.amount) }} /
                  {{ formatWeight(todayStats.sales.weight) }}
                </span>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-base font-semibold text-gray-700 mr-auto">Calls:</span>
              <div class="text-right">
                <span class="font-medium text-gray-700">
                  {{ todayStats.calls || 0 }}
                </span>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-base font-semibold text-gray-700 mr-auto">Emails:</span>
              <div class="text-right">
                <span class="font-medium text-gray-700">
                  {{ todayStats.emails || 0 }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </Card>

      <!-- This Week Summary -->
      <Card class="w-full">
        <template #title>
          <span class="text-green-800 text-center block">This Week</span>
        </template>
        <template #content>
          <div class="space-y-4">
            <div class="flex justify-between items-center">
              <span class="text-base font-semibold text-gray-700 mr-auto"
                >Purchases ({{ thisWeekStats.purchases.count }}):</span
              >
              <div class="text-right">
                <span class="font-medium text-gray-700">
                  {{ formatCurrency(thisWeekStats.purchases.amount) }} /
                  {{ formatWeight(thisWeekStats.purchases.weight) }}
                </span>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-base font-semibold text-gray-700 mr-auto"
                >Sales ({{ thisWeekStats.sales.count }}):</span
              >
              <div class="text-right">
                <span class="font-medium text-gray-700">
                  {{ formatCurrency(thisWeekStats.sales.amount) }} /
                  {{ formatWeight(thisWeekStats.sales.weight) }}
                </span>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-base font-semibold text-gray-700 mr-auto">Calls:</span>
              <div class="text-right">
                <span class="font-medium text-gray-700">
                  {{ thisWeekStats.calls || 0 }}
                </span>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-base font-semibold text-gray-700 mr-auto">Emails:</span>
              <div class="text-right">
                <span class="font-medium text-gray-700">
                  {{ thisWeekStats.emails || 0 }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </Card>

      <!-- This Month Summary -->
      <Card class="w-full">
        <template #title>
          <span class="text-purple-800 text-center block">This Month</span>
        </template>
        <template #content>
          <div class="space-y-4">
            <div class="flex justify-between items-center">
              <span class="text-base font-semibold text-gray-700 mr-auto"
                >Purchases ({{ thisMonthStats.purchases.count }}):</span
              >
              <div class="text-right">
                <span class="font-medium text-gray-700">
                  {{ formatCurrency(thisMonthStats.purchases.amount) }} /
                  {{ formatWeight(thisMonthStats.purchases.weight) }}
                </span>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-base font-semibold text-gray-700 mr-auto"
                >Sales ({{ thisMonthStats.sales.count }}):</span
              >
              <div class="text-right">
                <span class="font-medium text-gray-700">
                  {{ formatCurrency(thisMonthStats.sales.amount) }} /
                  {{ formatWeight(thisMonthStats.sales.weight) }}
                </span>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-base font-semibold text-gray-700 mr-auto">Calls:</span>
              <div class="text-right">
                <span class="font-medium text-gray-700">
                  {{ thisMonthStats.calls || 0 }}
                </span>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-base font-semibold text-gray-700 mr-auto">Emails:</span>
              <div class="text-right">
                <span class="font-medium text-gray-700">
                  {{ thisMonthStats.emails || 0 }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useTransactionStore, createTransaction } from '@/stores/transactionStore'
import { useCallLogStore } from '@/stores/callLogStore'
import { useEmailStore } from '@/stores/emailStore'
import DebugDialog from '@/components/debug/DebugDialog.vue'
import { TransactionModel } from '@/models/TransactionModel'
import type { TransactionRecord } from '@/types/Transaction'
import type { CallLog } from '@/types/CallLog'
import type { Email } from '@/stores/emailStore'
import Button from 'primevue/button'
import Card from 'primevue/card'

const router = useRouter()
const transactionStore = useTransactionStore()
const callLogStore = useCallLogStore()
const emailStore = useEmailStore()

const isDarkMode = ref(false)

// Check if we're in development mode
const isDev = computed(() => import.meta.env.DEV)

// Check for dark mode preference
const checkDarkMode = () => {
  isDarkMode.value = window.matchMedia('(prefers-color-scheme: dark)').matches
}

interface PeriodStats {
  purchases: { weight: number; amount: number; count: number }
  sales: { weight: number; amount: number; count: number }
  calls: number
  emails: number
}

const todayStats = ref<PeriodStats>({
  purchases: { weight: 0, amount: 0, count: 0 },
  sales: { weight: 0, amount: 0, count: 0 },
  calls: 0,
  emails: 0,
})

const thisWeekStats = ref<PeriodStats>({
  purchases: { weight: 0, amount: 0, count: 0 },
  sales: { weight: 0, amount: 0, count: 0 },
  calls: 0,
  emails: 0,
})

const thisMonthStats = ref<PeriodStats>({
  purchases: { weight: 0, amount: 0, count: 0 },
  sales: { weight: 0, amount: 0, count: 0 },
  calls: 0,
  emails: 0,
})

const calculateItemWeight = (item: any): number => {
  if (item.weight?.net !== null && item.weight?.net !== undefined) {
    return item.weight.net
  }
  if (item.weight?.gross !== null && item.weight?.tare !== null) {
    return (item.weight.gross || 0) - (item.weight.tare || 0)
  }
  return 0
}

const calculateStats = (
  transactions: TransactionRecord[],
  startDate: Date,
  endDate: Date,
): PeriodStats => {
  const filteredTransactions = transactions.filter((transaction) => {
    const transactionDate = new Date(transaction.transaction_date)
    return transactionDate >= startDate && transactionDate <= endDate
  })

  const purchases = filteredTransactions.filter((t) => t.transaction_type === 'purchase')
  const sales = filteredTransactions.filter((t) => t.transaction_type === 'sale')

  const purchaseWeight = purchases.reduce((sum, t) => {
    return sum + t.items.reduce((itemSum, item) => itemSum + calculateItemWeight(item), 0)
  }, 0)

  const purchaseAmount = purchases.reduce((sum, t) => sum + t.total_amount, 0)

  const salesWeight = sales.reduce((sum, t) => {
    return sum + t.items.reduce((itemSum, item) => itemSum + calculateItemWeight(item), 0)
  }, 0)

  const salesAmount = sales.reduce((sum, t) => sum + t.total_amount, 0)

  // Filter communications for the period
  const filteredCalls = callLogStore.items.filter((call: CallLog) => {
    return call.timestamp && call.timestamp >= startDate && call.timestamp <= endDate
  })

  const filteredEmails = emailStore.items.filter((email: Email) => {
    return email.sentAt && email.sentAt >= startDate && email.sentAt <= endDate
  })

  return {
    purchases: { weight: purchaseWeight, amount: purchaseAmount, count: purchases.length },
    sales: { weight: salesWeight, amount: salesAmount, count: sales.length },
    calls: filteredCalls.length,
    emails: filteredEmails.length,
  }
}

const loadStats = async () => {
  await transactionStore.load()
  await callLogStore.load()
  await emailStore.load()

  const now = new Date()

  // Today
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
  todayStats.value = calculateStats(transactionStore.items, todayStart, todayEnd)

  // This week (Monday to Sunday)
  const startOfWeek = new Date(todayStart)
  const dayOfWeek = startOfWeek.getDay()
  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
  startOfWeek.setDate(startOfWeek.getDate() + daysToMonday)

  const endOfWeek = new Date(startOfWeek)
  endOfWeek.setDate(startOfWeek.getDate() + 6)
  endOfWeek.setHours(23, 59, 59, 999)

  thisWeekStats.value = calculateStats(transactionStore.items, startOfWeek, endOfWeek)

  // This month
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)
  thisMonthStats.value = calculateStats(transactionStore.items, startOfMonth, endOfMonth)
}

const formatWeight = (weight: number): string => {
  if (weight === 0) return '0 lbs'
  return `${weight.toLocaleString()} lbs`
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)
}

// Navigation functions
async function navigateToNewTransaction() {
  const newTransaction = createTransaction()
  const savedTransaction = await transactionStore.add(newTransaction)
  router.push(`/transaction/${savedTransaction.id}/edit`)
}

function navigateToTransactions() {
  router.push('/transaction')
}

function navigateToBusinessOverview() {
  router.push('/reports/business-overview')
}

function navigateToTasks() {
  router.push('/crm/tasks')
}

function navigateToAdmin() {
  router.push('/admin')
}

onMounted(() => {
  // Check initial dark mode state
  checkDarkMode()

  // Listen for dark mode changes
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaQuery.addEventListener('change', checkDarkMode)

  loadStats()
})
</script>

<style scoped>
.home-view {
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 20px;
  box-shadow: none;
}

.cards-container :deep(.p-card) {
  min-width: 350px;
  max-width: 400px;
}

/* Dark mode for home view */
@media (prefers-color-scheme: dark) {
  .home-view {
    color: #f9fafb;
  }

  /* Dark mode for cards */
  .border {
    border-color: #4b5563 !important;
  }

  /* Dark mode for text colors */
  .text-gray-600 {
    color: #d1d5db !important;
  }

  .text-gray-700 {
    color: #f3f4f6 !important;
  }

  .text-gray-500 {
    color: #9ca3af !important;
  }

  /* Dark mode for Today card */
  .text-blue-800 {
    color: #93c5fd !important;
  }

  /* Dark mode for This Week card */
  .text-green-800 {
    color: #86efac !important;
  }

  /* Dark mode for This Month card */
  .text-purple-800 {
    color: #c4b5fd !important;
  }

  /* Dark mode for success/error colors */
  .text-green-600 {
    color: #34d399 !important;
  }

  .text-green-500 {
    color: #22c55e !important;
  }

  .text-red-600 {
    color: #f87171 !important;
  }

  .text-red-500 {
    color: #ef4444 !important;
  }

  /* Dark mode for borders */
  .border-t {
    border-color: #4b5563 !important;
  }

  /* Dark mode for buttons */
  :deep(.p-button-outlined) {
    color: #d1d5db !important;
    border-color: #6b7280 !important;
  }

  :deep(.p-button-outlined:hover) {
    background-color: #4b5563 !important;
    border-color: #9ca3af !important;
  }
}
</style>
